import Providers from "providers/Providers";
import WebAppLayout from "layout/WebAppLayout";
import ControlLayout from "layout/ControlLayout";
import MapEditorPage from "pages/map/MapEditorPage";
import ReduxStoreProvider from "providers/ReduxStoreProvider";
import ToastProvider from "providers/ToastProvider";
import NotificationProvider from "providers/NotificationProvider";
import FeaturesProvider from "providers/FeaturesProvider";
import DeviceFeatureFlagsProvider from "providers/DeviceFeatureFlagsProvider";
import GimbalControlProvider from "providers/GimbalControlProvider";
import Insta360SessionProvider from "providers/Insta360SessionProvider";
import ArmControlProvider from "providers/ArmControlProvider";
import InspectionProvider from "providers/InspectionProvider";
import NavigationProvider from "providers/navigation/NavigationProvider";
import RobotControlProvider from "providers/RobotControlProvider";
import MissionControlProvider from "providers/navigation/MissionProvider";
import MapProvider from "providers/navigation/MapProvider";
import MapManagerProvider from "providers/navigation/MapManagerProvider";
import MappingStatusProvider from "providers/navigation/MappingStatusProvider";
import TempMissionProvider from "providers/navigation/TempMissionProvider";
import LogHistoryProvider from "providers/LogHistoryProvider";
import StairsProvider from "providers/StairsProvider";
import DockingProvider from "providers/DockingProvider";
import MissionExecutionProvider from "providers/navigation/MissionExecutionProvider";
import NavigateToPoseProvider from "providers/navigation/NavigateToPoseProvider";
import BboxOverlaySourceProvider from "providers/BboxOverlaySourceProvider";
import WebRTCProvider from "providers/WebRTCProvider";
import WebRTCDebugProvider from "providers/WebRTCDebugProvider";
import LightControlProvider from "providers/ARC/LightControlProvider";
import EstopProvider from "providers/ARC/EstopProvider";
import IAQProvider from "providers/ARC/IAQProvider";
import CameraControlProvider from "providers/inspections/CameraControlProvider";
import ObjectDetectionProvider from "providers/inspections/ObjectionDetectionProvider";
import DeviationDetectionProvider from "providers/inspections/DeviationDetectionProvider";
import TakeReadingProvider from "providers/inspections/TakeReadingProvider";
import DeveloperModeProvider from "providers/DeveloperModeProvider";
import DevNavLogsProvider from "providers/DevNavLogsProvider";
import { LocalePreferencesProvider } from "providers/LocalePreferencesProvider";
import RobotProfileLivenessProvider from "providers/RobotProfileLivenessProvider";
import { controlRouteChildren } from "routes";

/**
 * Shared ARC control route factory used by both builds.
 *
 * @param {object} opts
 * @param {string} opts.basePath - Route path (e.g. "arc" or "fleet/arc/:arcId")
 * @param {React.ComponentType} opts.arcProvider - ARC context provider
 * @param {React.ComponentType[]} [opts.leadingProviders] - Extra providers above arcProvider
 * @param {React.ReactNode} [opts.controlElement] - Element for /control child; defaults to <ControlLayout />
 */
export const createArcControlRoute = ({
    basePath,
    arcProvider: ArcProvider,
    leadingProviders = [],
    controlElement,
}) => ({
    path: basePath,
    element: (
        <Providers
            key={`${basePath}-providers`}
            providers={[
                ReduxStoreProvider,
                LocalePreferencesProvider,
                NotificationProvider,
                ToastProvider,
                DeveloperModeProvider,
                ...leadingProviders,
                ArcProvider,
                DeviceFeatureFlagsProvider,
                DevNavLogsProvider,
                FeaturesProvider,
                GimbalControlProvider,
                Insta360SessionProvider,
                ArmControlProvider,
                InspectionProvider,
                NavigationProvider,
                RobotControlProvider,
                MissionControlProvider,
                MapProvider,
                // Docking gate for map load needs Docking + profile liveness above MapManager.
                RobotProfileLivenessProvider,
                DockingProvider,
                MapManagerProvider,
                MappingStatusProvider,
                TempMissionProvider,
                LogHistoryProvider,
                StairsProvider,
                MissionExecutionProvider,
                NavigateToPoseProvider,
                BboxOverlaySourceProvider,
                WebRTCProvider,
                WebRTCDebugProvider,
                LightControlProvider,
                EstopProvider,
                IAQProvider,
                CameraControlProvider,
                ObjectDetectionProvider,
                DeviationDetectionProvider,
                TakeReadingProvider,
            ]}
        >
            <WebAppLayout />
        </Providers>
    ),
    children: [
        {
            path: "control",
            element: controlElement ?? <ControlLayout />,
            children: controlRouteChildren,
        },
        {
            path: "map/editor",
            element: <MapEditorPage />,
        },
    ],
});
